import hash from 'object-hash';

export default function productForm({main, on, elm, state, publish}) {
	const form = elm.querySelector('#product-form');
	const addToCartBtn = elm.querySelector('#add-to-cart');
    const productImages = elm.querySelector('.product__images');
    const disableStickyBtn = elm.querySelector('[data-disable-sticky]');
    const stickyResizeTarget = productImages?.querySelector('.product__view') || productImages;
    const mobileStickyMedia = window.matchMedia('(max-width: 920px)');
	const slider = elm.querySelector('[data-slider="images"]');
    let optionsState = null;
    let availableState = null;
    let updateScheduled = false;
    let stickyToggleScheduled = false;

    main(_ => {
        init();

        on('change', 'input, select', scheduleUpdate);
		on('form-validation:submit', '#product-form', addToCart);
		on('click', '#quick-order', quickOrder);
        on('click', '[data-disable-sticky]', disableSticky);

        window.addEventListener('scroll', scheduleStickyToggle, { passive: true });
        window.addEventListener('resize', scheduleStickyToggle);
        mobileStickyMedia.addEventListener('change', updateStickyToggleState);
    });

    const init = function() {
        if (stickyResizeTarget) {
            const resizeObserver = new ResizeObserver(() => {
                scheduleStickyToggle();
            });

            resizeObserver.observe(stickyResizeTarget);
        }

        update();
        updateStickyToggleState();
    };

    const scheduleUpdate = function() {
        if (updateScheduled) return;

        updateScheduled = true;
        queueMicrotask(() => {
            updateScheduled = false;
            update();
        });
    };

    const update = function() {
        const selectedOptions = _getSelectedOptions();
        const renderState = calculatePrice(selectedOptions);

        emitOptionsChange(selectedOptions);
        changeVariant(selectedOptions);

		slider.swiper.slideTo(0, 400);

        renderState.finally(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    updateStickyToggleState();
                });
            });
        });
    }

    const calculatePrice = function(selectedOptions) {
        const {basePrice, combinations} = state.get();
        const selectedMap = _getSelectedMap(selectedOptions);
        const {price: overridePrice, combinationId} = _getOverridePrice(combinations, selectedMap);
        const productPrice = overridePrice || Number.parseFloat(basePrice);

		const optionPrices = [];

        const price = selectedOptions.reduce((acc, cur) => {
            const price = cur.dataset?.price ? Number.parseFloat(cur.dataset.price) : 0;
            let oldPrice = cur.dataset?.old_price ? Number.parseFloat(cur.dataset.old_price) : 0;
            oldPrice = oldPrice ? oldPrice : price;

            acc.current = acc.current + price;
            acc.old = acc.old + oldPrice;

			if (price) {
				const label = cur.closest('option-fieldset').dataset.name;
				optionPrices.push({label, price});
			}

			if (cur.dataset.optionId === '14' && productPrice) {
				const label = cur.closest('option-fieldset').dataset.name;
				optionPrices.push({label, price: productPrice});
			}

            return acc;
        }, {current: productPrice, old: productPrice});

        return state.set(s => {
            s.price = price.current;
            s.oldPrice = price.old;
            s.combinationId = combinationId;
			s.optionPrices = optionPrices;
        });
    };

    const emitOptionsChange = function(selectedOptions) {
        const data = selectedOptions.reduce((acc, cur) => {
            const option = cur.dataset.option_code;
            if (!option) return acc;

            const value = cur.dataset.value_code;
            acc.constructor.push(`${option}:${value}`);

            const color = cur.dataset.color;
            if (color) acc.colors[option] = color;

            const image = cur.dataset.image;
            if (image) acc.images[option] = image;

            return acc;
        }, {constructor: [], colors: {}, images: {}});

        const newOptionsState = JSON.stringify(data);
        if (newOptionsState === optionsState) return;

        optionsState = newOptionsState;
        publish('options-change', data);
    }

    const changeVariant = function(selectedOptions) {
        const { combinations } = state.get();
        const selectedMap = _getSelectedMap(selectedOptions);
        const available = _buildAvailability(combinations, selectedMap);

        const newAvailableState = hash(available);
        if (newAvailableState === availableState) return;

        availableState = newAvailableState;
        publish('available-values', available);
    };

	const addToCart = async function(e, { data }) {
		const formData = new FormData();
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});

		const action = 'index.php?route=checkout/cart.add';

		try {
			addToCartBtn.disabled = true;
			const response = await fetch(action, {
				method: 'POST',
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`Request failed: ${response.status}`);
			}

			const result = await response.json();

			const miniCart = document.querySelector('cart-mini');
			await miniCart.update();
			miniCart.display();

			const addToCartEvent = new CustomEvent('add-to-cart', { detail: formData });
			document.dispatchEvent(addToCartEvent);
		} catch (e) {
			console.error(e);
		} finally {
			addToCartBtn.disabled = false;
		}
	}

	const quickOrder = function() {
		const model = state.get();
		const formData = new FormData(form);

		const data = {
			product: model,
			form: Object.fromEntries(formData),
		};
		publish('quick-order', data);
	};

    const disableSticky = function() {
        if (!productImages || !disableStickyBtn) return;

        productImages.classList.add('product__images--static');
        updateStickyToggleState();
    };

    const scheduleStickyToggle = function() {
        if (stickyToggleScheduled) return;

        stickyToggleScheduled = true;
        requestAnimationFrame(() => {
            stickyToggleScheduled = false;
            updateStickyToggleState();
        });
    };

    const updateStickyToggleState = function() {
        if (!productImages || !disableStickyBtn) return;

        disableStickyBtn.hidden = !_isStickyActive();
    };

    const _isStickyActive = function() {
        if (!mobileStickyMedia.matches || productImages.classList.contains('product__images--static')) {
            return false;
        }

        const styles = window.getComputedStyle(productImages);
        if (styles.position !== 'sticky' || window.scrollY <= 0) {
            return false;
        }

        const stickyTop = Number.parseFloat(styles.top) || 0;
        const currentTop = productImages.getBoundingClientRect().top;

        return currentTop <= stickyTop + 1;
    };

    const _getSelectedOptions = function() {
        return [...elm.querySelectorAll('input[type="radio"]:checked, select option:checked:not([value=""])')];
    };

    const _getSelectedMap = function(selectedOptions) {
        return selectedOptions.reduce((acc, input) => {
            acc[input.dataset.optionId] = input.dataset.valueId;
            return acc;
        }, {});
    };

    const _getOverridePrice = function(combinations, selectedMap) {
        for (const combination_id in combinations) {
			const combination = combinations[combination_id];
            const price = Number.parseFloat(combination.price);
            if (!price) continue;

            let status = true;
            for (const {option_id, values} of Object.values(combination.options)) {
                if (!values.includes(selectedMap[option_id])) status = false;
            }

            if (status) {
	            return {
                    price,
                    combinationId: combination_id,
                };
            }
        }

        return {
            price: false,
            combinationId: 0,
        };
    };

    const _buildAvailability = function(combinations, selectedMap) {
        const optionIds = new Set();
        const optionValues = {};

        for (const combination of Object.values(combinations)) {
            for (const [optionId, option] of Object.entries(combination.options)) {
                optionIds.add(optionId);

                if (!optionValues[optionId]) {
                    optionValues[optionId] = new Set();
                }

                for (const valueId of option.values) {
                    optionValues[optionId].add(valueId);
                }
            }
        }

        const undependedOptions = ['13','16'];

        const available = {};
        for (const optionId of optionIds) {
            available[optionId] = new Set();

            for (const valueId of optionValues[optionId]) {
                if (_hasCompatibleCombination(combinations, selectedMap, optionId, valueId) || undependedOptions.includes(optionId)) {
                    available[optionId].add(valueId);
                }
            }
        }

        return available;
    };

    const _hasCompatibleCombination = function(combinations, selectedMap, targetOptionId, targetValueId) {
        for (const {options} of Object.values(combinations)) {
            const targetOption = options[targetOptionId];
            if (!targetOption || !targetOption.values.includes(targetValueId)) continue;

            let isValid = true;

            for (const [selectedOptionId, selectedValueId] of Object.entries(selectedMap)) {
                if (selectedOptionId === targetOptionId) continue;

                const option = options[selectedOptionId];

                if (!option) continue;

                if (!option.values.includes(selectedValueId)) {
                    isValid = false;
                    break;
                }
            }

            if (isValid && options[targetOptionId]?.values?.includes(targetValueId)) {
                return true;
            }
        }

        return false;
    }
}

export const model = {
    price: 0,
    oldPrice: 0,
	combinationId: 0,
	options: [],
	optionPrices: []
};

export const view = (state) => {
	const numFormater = new Intl.NumberFormat('uk-UA');

    return {
        ...state,
        displayPrice: numFormater.format(state.price) + ' zł',
        displayOldPrice: numFormater.format(state.oldPrice) + ' zł',
        hasDiscount: state.oldPrice > state.price,
        discount: 100 - Math.round(state.oldPrice / state.price * 100) + '%'
    }
}
